import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Phone, 
  Mail, 
  MessageSquare, 
  HelpCircle, 
  ChevronDown, 
  ArrowRight,
  ShieldCheck,
  CreditCard,
  Calendar,
  MapPin,
  LifeBuoy
} from 'lucide-react';
import SEO from '../components/Common/SEO';
import ScrollToTop from '../components/Common/ScrollToTop';

const Help: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);

  const faqCategories = [
    {
      id: 'booking',
      title: 'Booking & Reservations',
      icon: <CreditCard className="w-6 h-6" />,
      faqs: [
        {
          id: 'b1',
          question: 'How do I book a safari tour?',
          answer: 'You can book a safari tour through our website by selecting your preferred tour, choosing your dates, and completing the booking form. Our interactive Booking Guide provides a step-by-step walkthrough of the entire process.'
        },
        {
          id: 'b2',
          question: 'What payment methods do you accept?',
          answer: 'We accept all major global credit cards (Visa, MasterCard, American Express), international bank transfers, and PayPal. For East African travelers, we also support regional mobile money solutions.'
        },
        {
          id: 'b3',
          question: 'What is your cancellation policy?',
          answer: 'We offer flexible cancellation. Full refunds are available for cancellations made 60+ days before travel. Partial refunds apply for shorter windows: 75% for 30-59 days, and 50% for 15-29 days before departure.'
        }
      ]
    },
    {
      id: 'travel',
      title: 'Travel & Preparation',
      icon: <Calendar className="w-6 h-6" />,
      faqs: [
        {
          id: 't1',
          question: 'What should I pack for my safari?',
          answer: 'We recommend neutral-colored clothing, sturdy walking boots, and layers for shifting dawn-to-dusk temperatures. A detailed, professional packing list is provided immediately upon booking confirmation.'
        },
        {
          id: 't2',
          question: 'Do I need vaccinations for East Africa?',
          answer: 'Yes, specific vaccinations including Yellow Fever are required. We recommend consulting a travel health professional 4-6 weeks prior to your expedition for the latest regional requirements.'
        },
        {
          id: 't3',
          question: 'When is the best time for a safari?',
          answer: 'Dry seasons (June-October and December-March) offer peak wildlife visibility. However, the Green Season (November-May) provides spectacular conditions for photography and birding with fewer crowds.'
        }
      ]
    },
    {
      id: 'safety',
      title: 'Safety & Professionalism',
      icon: <ShieldCheck className="w-6 h-6" />,
      faqs: [
        {
          id: 's1',
          question: 'Is it safe to travel to Kenya and Tanzania?',
          answer: 'Absolutely. We operate in secure, globally-recognized destinations and partner with premier lodges. Our guides are regional experts who prioritize guest safety above all else.'
        },
        {
          id: 's2',
          question: 'What emergency protocols are in place?',
          answer: 'All expedition vehicles are equipped with satellite communication, first aid kits, and 24/7 links to our Nairobi headquarters. Our guides are certified in wilderness first response.'
        }
      ]
    }
  ];

  const contactMethods = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Voice Support',
      desc: 'Speak with a safari architect',
      contact: '+254 708 560 665',
      tag: '24/7 Global Line'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email Intelligence',
      desc: 'Send detailed inquiries',
      contact: 'concierge@sighttrailsafaris.com',
      tag: '24hr Response Time'
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'Live Concierge',
      desc: 'Instant real-time assistance',
      contact: 'Available Online',
      tag: 'Fastest Response'
    }
  ];

  const popularTopics = [
    'Safari Booking',
    'Packing Guide',
    'Best Time to Visit',
    'Visa Protocols',
    'Health & Safety',
    'Payment Systems',
    'Airports & Transfers'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  } as const;

  return (
    <div className="min-h-screen bg-[#F8FBFF] font-sans selection:bg-blue-100">
      <SEO 
        title="Help Center | SightTrail Safaris" 
        description="Get the support and answers you need for your premium East African safari experience."
      />

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 px-6 overflow-hidden bg-white">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <svg width="100%" height="100%"><rect width="100%" height="100%" fill="url(#help-grid)" /></svg>
          <defs>
            <pattern id="help-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
            </pattern>
          </defs>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6 group cursor-default">
            <LifeBuoy className="w-4 h-4 text-blue-500 mr-2 animate-spin-slow" />
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest luxury-font">Assistant Center</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight font-serif mb-6">
            How can we <span className="text-blue-500">assist you?</span>
          </h1>
          
          <div className="mt-10 max-w-2xl mx-auto relative group">
            <div className="absolute inset-0 bg-blue-500/5 blur-2xl group-hover:bg-blue-500/10 transition-all duration-500 rounded-full" />
            <div className="relative">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input 
                type="text"
                placeholder="Search for articles, protocols, or advice..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-16 pr-8 py-6 rounded-full bg-white border border-slate-100 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.05)] focus:shadow-[0_20px_50px_-10px_rgba(59,130,246,0.15)] focus:border-blue-200 outline-none transition-all text-sm font-medium"
              />
            </div>
          </div>
        </motion.div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-32">
        {/* Popular Tags */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap justify-center gap-3 mt-12 mb-24"
        >
          {popularTopics.map((topic, i) => (
            <button key={i} className="px-5 py-2.5 rounded-full bg-white border border-slate-50 text-[11px] font-bold text-slate-500 uppercase tracking-wider hover:bg-blue-50 hover:border-blue-100 hover:text-blue-600 transition-all shadow-sm">
              {topic}
            </button>
          ))}
        </motion.div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-12">
            <h2 className="text-2xl font-extrabold text-slate-900 font-serif flex items-center">
              <HelpCircle className="w-6 h-6 text-blue-500 mr-3" />
              Frequently Asked Questions
            </h2>

            <motion.div 
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-6"
            >
              {faqCategories.map((cat) => (
                <motion.div key={cat.id} variants={itemVariants} className="space-y-4">
                  <div className="flex items-center space-x-3 text-blue-600 mb-6">
                    {cat.icon}
                    <span className="text-xs font-bold uppercase tracking-widest luxury-font">{cat.title}</span>
                  </div>
                  
                  <div className="space-y-4">
                    {cat.faqs.map((faq) => (
                      <div 
                        key={faq.id}
                        className={`group rounded-[24px] border transition-all duration-300 ${expandedFaq === faq.id ? 'bg-white border-blue-100 shadow-xl' : 'bg-white/50 border-slate-50 hover:border-blue-50 hover:bg-white'}`}
                      >
                        <button 
                          onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                          className="w-full px-8 py-6 flex items-center justify-between text-left"
                        >
                          <span className={`font-bold transition-colors ${expandedFaq === faq.id ? 'text-blue-600' : 'text-slate-900'}`}>{faq.question}</span>
                          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${expandedFaq === faq.id ? 'rotate-180 text-blue-500' : ''}`} />
                        </button>
                        
                        <AnimatePresence>
                          {expandedFaq === faq.id && (
                            <motion.div 
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="px-8 pb-8 pt-2 text-sm text-slate-500 leading-relaxed font-medium">
                                {faq.answer}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Sidebar / Contact */}
          <div className="lg:col-span-4 space-y-8">
            <div className="sticky top-32 space-y-8">
              <div className="p-10 rounded-[40px] bg-white border border-blue-50 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.05)]">
                <h3 className="text-xl font-extrabold text-slate-900 font-serif mb-6">Concierge Support</h3>
                <div className="space-y-6">
                  {contactMethods.map((method, i) => (
                    <div key={i} className="flex items-start space-x-4 group cursor-pointer">
                      <div className="p-3 rounded-2xl bg-blue-50 text-blue-500 group-hover:bg-blue-500 group-hover:text-white transition-all duration-300">
                        {method.icon}
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-1">{method.tag}</p>
                        <p className="font-bold text-slate-900">{method.title}</p>
                        <p className="text-[11px] text-slate-400 font-medium mb-1">{method.desc}</p>
                        <p className="text-xs font-black text-slate-600 group-hover:text-blue-600 transition-colors uppercase tracking-tight">{method.contact}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Emergency Banner */}
              <div className="p-8 rounded-[32px] bg-red-50 border border-red-100 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                <div className="relative z-10">
                  <div className="flex items-center text-red-600 mb-4 font-black uppercase text-[10px] tracking-[0.2em]">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Priority Defense
                  </div>
                  <h4 className="text-lg font-bold text-red-900 mb-2">Emergency Hub</h4>
                  <p className="text-red-700/70 text-xs font-bold leading-relaxed mb-6">Active safari emergencies only. Available 24/7 for immediate field support.</p>
                  <a href="tel:+254708560665" className="flex items-center justify-between w-full p-4 bg-white rounded-2xl shadow-sm text-red-600 group-hover:shadow-lg transition-all">
                    <span className="text-sm font-black">+254 708 560 665</span>
                    <Phone className="w-4 h-4 animate-pulse" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Global CTA */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mt-40 relative rounded-[48px] overflow-hidden group shadow-3xl bg-slate-900 p-12 md:p-24 text-center"
        >
          <div className="absolute inset-0 opacity-20 transition-transform duration-[5s] group-hover:scale-110">
            <img 
              src="https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?q=80&w=2671&auto=format&fit=crop" 
              className="w-full h-full object-cover"
              alt="Safari Landscape"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
          
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-extrabold text-white font-serif mb-8 leading-tight">
              Ready to architect your <span className="text-blue-400 italic">perfect safari?</span>
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <button className="w-full sm:w-auto px-10 py-5 bg-blue-500 text-white rounded-full font-bold uppercase tracking-widest text-xs hover:bg-blue-600 transition-all shadow-2xl shadow-blue-500/40">
                Book a Consultation
              </button>
              <button className="w-full sm:w-auto px-10 py-5 bg-white/10 backdrop-blur-md text-white border border-white/20 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-white hover:text-slate-900 transition-all flex items-center justify-center">
                Download Full Guide
                <ArrowRight className="w-4 h-4 ml-3" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
      
      <ScrollToTop />
      
      <style>{`
        .animate-spin-slow {
          animation: spin 8s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .luxury-font {
          letter-spacing: 0.25em;
        }
      `}</style>
    </div>
  );
};

export default Help;
